import React, { useCallback, useState, useLayoutEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import arrayMutators from 'final-form-arrays';
import { Form } from 'react-final-form';
import createDecorator from 'final-form-calculate';

import { useDeepEqualSelector } from 'Shared/utils/hooks';
import Spinner from 'Shared/components/Spinner';
import { bulkCreateWorkspaces, bulkUpdateWorkspaces } from 'Shared/actions/workspaces';
import { getWorkspaceTeammatesIds, getWorkspacesWithoutRecurringMeeting } from 'Shared/selectors/workspace';
import { RecurringMeeting } from 'Shared/constants/types';
import { WpModal, WpModalHeader, WpModalFooter, WpModalBody } from 'Wpds/components/modal';

import ButtonsArea from './ButtonsArea/ButtonsArea';
import LinkExistingMeetingsFormFields from './LinkExistingMeetingsFormFields/LinkExistingMeetingsFormFields';
import CreateTeammatesWorkspacesFormFields from './CreateTeammatesWorkspacesFormFields/CreateTeammatesWorkspacesFormFields';
import CreateGroupWorkspacesFormFields from './CreateGroupWorkspacesFormFields/CreateGroupWorkspacesFormFields';
import InviteTeammatesFormFields from './InviteTeammatesFormFields/InviteTeammatesFormFields';
import StepsSection from './StepsSection/StepsSection';
import { SyncMeetingsSteps } from '../types';
import {
  getRecurringMeetingsForSyncMeetingStep,
  getModalTitle,
  generateWorkspacesParams,
  generateLinkedWorkspacesParams,
  getInitialWorkspaces,
  getInitialInviteeEmails,
  selectedRecurringMeetingsIdsDecorator,
} from './utils';
import TrackingConstants from 'Shared/constants/Tracking.constants';

import './SyncMeetingsFlowForm.scss';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

const getPersonalMessage = ({ selectedOnlyGroupMeetings, tr }) =>
  selectedOnlyGroupMeetings
    ? tr('perform.sync_meetings.personal_message.default_generic')
    : tr('perform.sync_meetings.personal_message.default_11');

const decorators = [
  createDecorator({
    field: 'workspaces',
    updates: {
      selectedRecurringMeetingsIds: selectedRecurringMeetingsIdsDecorator,
    },
  }),
];

type Props = {
  onToggle: Function;
  onDisplaySuccessMessage?: Function;
  isLoading: boolean;
  steps: Array<SyncMeetingsSteps>;
  recurringMeetings: Array<RecurringMeeting>;
  customFilterForOneOnOneSuggestions?: Function;
  customFilterForGroupSuggestions?: Function;
};

function SyncMeetingsFlowForm({
  onToggle,
  isLoading,
  steps: initialSteps,
  recurringMeetings,
  customFilterForOneOnOneSuggestions,
  customFilterForGroupSuggestions,
  onDisplaySuccessMessage,
}: Props) {
  const dispatch = useDispatch();
  const { user, id: currentUserId } = useCurrentUser();
  const currentUserEmail = user.email;
  const memberIds = useSelector(state => state.members.map(({ id }) => id));
  const workspaces = useDeepEqualSelector(getWorkspacesWithoutRecurringMeeting());
  const teammatesIds = useDeepEqualSelector(getWorkspaceTeammatesIds);
  const { tr } = useTranslation();

  const [steps, setSteps] = useState(initialSteps);
  const [currentStep, setCurrentStep] = useState(steps[0]);
  const [linkedWorkspaces, setLinkedWorkspaces] = useState(null);
  const [selectedTeammateWorkspaces, setSelectedTeammateWorkspaces] = useState(null);
  const [selectedGroupWorkspaces, setSelectedGroupWorkspaces] = useState(null);

  const filteredRecurringMeetings = getRecurringMeetingsForSyncMeetingStep({
    recurringMeetings,
    currentStep,
    teammatesIds,
    currentUserId,
    linkedWorkspaces,
    selectedTeammateWorkspaces,
    selectedGroupWorkspaces,
    customFilterForOneOnOneSuggestions,
    customFilterForGroupSuggestions,
  });
  const initialInviteeEmails = getInitialInviteeEmails({
    selectedTeammateWorkspaces,
    selectedGroupWorkspaces,
    currentUserEmail,
    linkedWorkspaces,
    memberIds,
  });
  const selectedOnlyGroupMeetings = !selectedTeammateWorkspaces?.length && selectedGroupWorkspaces?.length;

  const handleBackClick = useCallback(() => setCurrentStep(step => steps[steps.indexOf(step) - 1]), [steps]);

  const handleNextStep = useCallback(() => setCurrentStep(step => steps[steps.indexOf(step) + 1]), [steps]);

  const handleLinkExistingMeetings = useCallback(
    async values => {
      const selectedWorkspaces = values.workspaces.filter(({ recurringMeeting }) => recurringMeeting?.id);
      setLinkedWorkspaces(generateLinkedWorkspacesParams({ selectedWorkspaces, currentUserId }));
      return handleNextStep();
    },
    [handleNextStep, currentUserId]
  );

  const handleAddTeammateWorkspaces = useCallback(
    values => {
      const workspacesParams = generateWorkspacesParams({
        recurringMeetings: values.recurringMeetings.filter(({ selected }) => selected),
        currentUserId,
        isGroup: false,
      });
      setSelectedTeammateWorkspaces(workspacesParams);
      return handleNextStep();
    },
    [handleNextStep, currentUserId]
  );

  const handleAddGroupWorkspaces = useCallback(
    values => {
      const workspacesParams = generateWorkspacesParams({
        recurringMeetings: values.recurringMeetings.filter(({ selected }) => selected),
        currentUserId,
        isGroup: true,
      });
      setSelectedGroupWorkspaces(workspacesParams);
      return handleNextStep();
    },
    [handleNextStep, currentUserId]
  );

  const handleFinishFlow = useCallback(
    async values => {
      const selectedEmails = values.inviteeEmails.filter(({ selected }) => selected).map(({ email }) => email);
      let redirectPath;
      const workspacesToUpdateParams = linkedWorkspaces?.map(({ recurringMeeting, id, inviteeUsers }) => {
        const emailsWhiteList = inviteeUsers
          .filter(({ email }) => selectedEmails.includes(email))
          .map(({ email }) => email);
        return {
          id,
          recurringMeetingId: recurringMeeting.id,
          inviteeEmails: emailsWhiteList,
          personalMessage: values.personalMessage,
          inviteeEmailsFromCalendar: emailsWhiteList,
        };
      });
      const workspacesToCreateParams = [...(selectedTeammateWorkspaces || []), ...(selectedGroupWorkspaces || [])].map(
        ({ name, isGroup, recurringMeetingId, inviteeUsers }) => {
          const emailsWhiteList = inviteeUsers
            .filter(({ email }) => selectedEmails.includes(email))
            .map(({ email }) => email);
          return {
            name,
            isGroup,
            recurringMeetingId,
            inviteeEmails: emailsWhiteList,
            personalMessage: values.personalMessage,
            inviteeEmailsFromCalendar: emailsWhiteList,
          };
        }
      );

      try {
        if (workspacesToUpdateParams?.length)
          await dispatch(bulkUpdateWorkspaces({ workspacesParams: workspacesToUpdateParams, throwError: true }));
        if (workspacesToCreateParams.length) {
          const firstCreatedWorkspace = await dispatch(
            bulkCreateWorkspaces({
              workspacesParams: workspacesToCreateParams,
              inviteMethod: TrackingConstants.INVITE_METHOD_TYPES.INVITE_FROM_CALENDAR_INTEGRATION,
              showDefaultSuccessMessage: false,
              doNotTerminateOnErrors: true,
              throwError: true,
            })
          );
          if (firstCreatedWorkspace?.path) redirectPath = firstCreatedWorkspace?.path;
        }
        if (onDisplaySuccessMessage) onDisplaySuccessMessage();
        if (!values.skipToggleAction) return onToggle({ redirectPath, finishButtonClicked: true });
        return null;
      } catch (error) {
        return error;
      }
    },
    [dispatch, onToggle, linkedWorkspaces, selectedTeammateWorkspaces, selectedGroupWorkspaces, onDisplaySuccessMessage]
  );

  useLayoutEffect(() => {
    switch (currentStep) {
      case SyncMeetingsSteps.linkExistingMeetings: {
        if (!workspaces?.length && !isLoading) {
          setSteps(steps.filter(step => step !== SyncMeetingsSteps.linkExistingMeetings));
          handleNextStep();
        }
        break;
      }
      case SyncMeetingsSteps.createNewTeammateWorkspaces:
      case SyncMeetingsSteps.newOneOnOneMeetings: {
        if (!filteredRecurringMeetings?.length && !isLoading) {
          setSteps(
            steps.filter(
              step =>
                step !== SyncMeetingsSteps.newOneOnOneMeetings && step !== SyncMeetingsSteps.createNewTeammateWorkspaces
            )
          );
          handleNextStep();
        }
        break;
      }
      case SyncMeetingsSteps.createNewGroupWorkspaces: {
        if (!filteredRecurringMeetings?.length && !isLoading) {
          setSteps(steps.filter(step => step !== SyncMeetingsSteps.createNewGroupWorkspaces));
          handleNextStep();
        }
        break;
      }
      case SyncMeetingsSteps.inviteTeammates: {
        if (!initialInviteeEmails?.length) {
          onToggle({ autoComplete: true, finishButtonClicked: true });
          handleFinishFlow({
            inviteeEmails: initialInviteeEmails,
            personalMessage: getPersonalMessage({ selectedOnlyGroupMeetings, tr }),
            skipToggleAction: true,
          });
        }
        break;
      }
      default:
        break;
    }
  }, [currentStep, isLoading]);

  const getSubmitFunction = useCallback(
    step => {
      switch (step) {
        case SyncMeetingsSteps.linkExistingMeetings:
          return handleLinkExistingMeetings;
        case SyncMeetingsSteps.createNewTeammateWorkspaces:
        case SyncMeetingsSteps.newOneOnOneMeetings:
          return handleAddTeammateWorkspaces;
        case SyncMeetingsSteps.createNewGroupWorkspaces:
          return handleAddGroupWorkspaces;
        case SyncMeetingsSteps.inviteTeammates:
          return handleFinishFlow;
        default:
          return () => onToggle();
      }
    },
    [onToggle, handleLinkExistingMeetings, handleAddTeammateWorkspaces, handleAddGroupWorkspaces, handleFinishFlow]
  );

  const handleKeyPress = useCallback(event => {
    if (event.which === 13 /* Enter */) event.preventDefault();
  }, []);

  return (
    <Form
      mutators={{
        ...arrayMutators,
      }}
      initialValues={{
        workspaces: getInitialWorkspaces({ workspaces, linkedWorkspaces, recurringMeetings }),
        recurringMeetings: filteredRecurringMeetings,
        personalMessage: getPersonalMessage({ selectedOnlyGroupMeetings, tr }),
        inviteeEmails: initialInviteeEmails,
      }}
      subscription={{ values: false }}
      decorators={decorators}
      onSubmit={getSubmitFunction(currentStep)}
    >
      {({ handleSubmit }) => (
        <form onKeyPress={handleKeyPress} onSubmit={handleSubmit} autoComplete="off">
          <WpModal size="md" isOpen qaTest="SyncMeetingsModal" onToggle={() => onToggle()} dismissOnBackdrop={false}>
            <WpModalHeader>{getModalTitle(currentStep)}</WpModalHeader>
            <StepsSection steps={steps} currentStep={currentStep} />
            <WpModalBody>
              <div className="SyncMeetingsFlowForm">
                {isLoading && <Spinner activeIf className="p-5" />}
                {!isLoading && currentStep === SyncMeetingsSteps.linkExistingMeetings && (
                  <LinkExistingMeetingsFormFields />
                )}
                {!isLoading &&
                  (currentStep === SyncMeetingsSteps.createNewTeammateWorkspaces ||
                    currentStep === SyncMeetingsSteps.newOneOnOneMeetings) && (
                    <CreateTeammatesWorkspacesFormFields currentStep={currentStep} />
                  )}
                {!isLoading && currentStep === SyncMeetingsSteps.createNewGroupWorkspaces && (
                  <CreateGroupWorkspacesFormFields />
                )}
                {!isLoading && currentStep === SyncMeetingsSteps.inviteTeammates && <InviteTeammatesFormFields />}
              </div>
            </WpModalBody>
            {!isLoading && (
              <WpModalFooter>
                <ButtonsArea
                  onSubmit={handleSubmit}
                  onBackClick={handleBackClick}
                  onNextStep={handleNextStep}
                  currentStep={currentStep}
                  steps={steps}
                />
              </WpModalFooter>
            )}
          </WpModal>
        </form>
      )}
    </Form>
  );
}

export default SyncMeetingsFlowForm;
